import { useDynamicTranslations } from '../hooks/useDynamicTranslations';

const Footer = () => {
  const { t } = useDynamicTranslations();
  
  const currentYear = new Date().getFullYear();

  // Get dynamic content from CMS
  const companyDescription = t('footer.companyDescription') || 'Eesti metsandussektori juhtiv partner juba 20 aastat. Pakume kvaliteetseid teenuseid metsamaa ja metsamaterjali ostust-müügist kuni transportini üle kogu Eesti.';
  const vatInfo = t('footer.vatInfo') || 'Käibemaksukohustuslane';
  const kmkrNumber = t('footer.kmkrNumber') || 'KMKR nr EE123456789';
  const businessHours = t('footer.businessHours') || 'Lahtiolekuajad: E-R 8:00-17:00';
  const address = t('footer.contact.address') || 'Osmussaare tänav 8, 13811 Tallinn, Eesti';
  const phone1 = t('footer.contact.phone1') || '+372 6707 110';
  const phone2 = t('footer.contact.phone2') || '+372 5663 6076';
  const email = t('footer.contact.email') || 'info@forestryest.ee';
  
  // Get copyright text and replace {year} placeholder with current year
  const copyrightTemplate = t('footer.copyright') || '© {year} Forestry OÜ. Kõik õigused kaitstud. Reg.nr 12345678';
  const copyright = copyrightTemplate.replace('{year}', currentYear.toString());

  // Calculate company years for description
  const companyYears = currentYear - 2005;
  const companyDescriptionWithYears = companyDescription.replace('{years}', companyYears.toString());

  // Define the actual quick links for the website
  const quickLinks = [
    { name: "Avaleht", url: "#hero" },
    { name: "Teenused", url: "#services" },
    { name: "Meie Ettevõte", url: "#about" },
    { name: "Kontakt", url: "#contact" }
  ];

  // Define the actual contact information
  const contactInfo = [
    { 
      name: address, 
      url: "#contact",
      icon: "location"
    },
    { 
      name: phone1, 
      url: `tel:${phone1.replace(/\s/g, '')}`,
      icon: "phone"
    },
    { 
      name: phone2, 
      url: `tel:${phone2.replace(/\s/g, '')}`,
      icon: "phone"
    },
    { 
      name: email, 
      url: `mailto:${email}`,
      icon: "email"
    }
  ];

  const getIcon = (iconType: string) => {
    switch (iconType) {
      case 'location':
        return (
          <svg className="h-5 w-5 text-primary-400 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
          </svg>
        );
      case 'phone':
        return (
          <svg className="h-5 w-5 text-primary-400 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z"></path>
          </svg>
        );
      case 'email':
        return (
          <svg className="h-5 w-5 text-primary-400 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"></path>
            <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"></path>
          </svg>
        );
      default:
        return (
          <svg className="h-5 w-5 text-primary-400 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.707l-3-3a1 1 0 00-1.414 0l-3 3a1 1 0 001.414 1.414L9 9.414V13a1 1 0 102 0V9.414l1.293 1.293a1 1 0 001.414-1.414z" clipRule="evenodd" />
          </svg>
        );
    }
  };

  return (
    <footer className="bg-primary-900 text-white pt-16 pb-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12 mb-12">
          {/* Company info */}
          <div>
            <div className="flex items-center mb-6">
              <span className="text-xl font-heading font-bold">Forestry OÜ</span>
            </div>
            <p className="text-gray-300 mb-4 leading-relaxed">
              {companyDescriptionWithYears}
            </p>
            <div className="text-sm text-gray-400 space-y-1">
              <p>{vatInfo}</p>
              <p className="font-medium">{kmkrNumber}</p>
            </div>
          </div>

          {/* Quick links */}
          <div>
            <h3 className="text-lg font-bold mb-6 font-heading">Kiirlingid</h3>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <a href={link.url} className="text-gray-300 hover:text-white transition-colors">
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-lg font-bold mb-6 font-heading">Kontakt</h3>
            <ul className="space-y-4">
              {contactInfo.map((contact, index) => (
                <li key={index} className="flex items-start">
                  {getIcon(contact.icon)}
                  <a href={contact.url} className="text-gray-300 hover:text-white transition-colors text-sm">
                    {contact.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-primary-800 pt-8">
          <div className="md:flex md:items-center md:justify-between">
            <div className="text-sm text-gray-400">
              <p className="mb-2 md:mb-0">
                {copyright}
              </p>
            </div>
            <div className="mt-4 md:mt-0">
              <p className="text-xs text-gray-500">
                {businessHours}
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;